/**
 * Componenti riutilizzabili per gli alert di food cost / variazioni costi.
 * - useCostAlerts(): hook che carica GET /api/costi/alert (si auto-nasconde su 403).
 * - CostAlertBanner: banner per le pagine Ricette / Menu (elenca gli item rincarati).
 * - CostAlertCard: card per la Dashboard con riepilogo + storico (grafico).
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { TrendingUp, AlertTriangle, X, LineChart as LineChartIcon, ArrowRight, Lightbulb, Package, Carrot } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const t = sessionStorage.getItem("token") || localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export function useCostAlerts(localeId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = localeId ? `?locale_id=${localeId}` : "";
      const res = await axios.get(`${API}/costi/alert${q}`, { headers: authHeaders() });
      setData(res.data);
      setForbidden(false);
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [localeId]);

  useEffect(() => { load(); }, [load]);
  return { data, loading, forbidden, reload: load };
}

/** Mappe id→alert per ancorare gli avvisi alle singole card (Ricette/Menu). */
export function useCostAlertMaps(localeId) {
  const { data, loading, forbidden, reload } = useCostAlerts(localeId);
  const ricetteById = {};
  (data?.ricette || []).forEach((a) => { ricetteById[a.id] = a; });
  const piattiById = {};
  (data?.piatti || []).forEach((a) => { piattiById[a.id] = a; });
  return { ricetteById, piattiById, soglia: data?.soglia_pct, loading, forbidden, reload };
}

/** Banner per pagine Ricette/Menu. tipo = "piatti" | "ricette" */
export function CostAlertBanner({ tipo = "piatti", localeId }) {
  const { data, forbidden } = useCostAlerts(localeId);
  const [closed, setClosed] = useState(false);
  if (forbidden || closed || !data) return null;

  // Banner "food cost in aumento": solo gli AUMENTI (un calo non è un'allerta)
  const items = (tipo === "ricette" ? (data.ricette || []) : (data.piatti || [])).filter((x) => (x.delta_pct || 0) > 0);
  if (items.length === 0) return null;
  const soglia = data.soglia_pct ?? 5;

  return (
    <div
      className="rounded-2xl border border-amber-300 bg-amber-50 p-4 mb-4"
      data-testid={`cost-alert-banner-${tipo}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-bold text-amber-900 text-sm">
              {items.length} {tipo === "ricette" ? "ricett" : "piatt"}{items.length === 1 ? "o" : "i"} con food cost in aumento (&gt;{soglia}%)
            </h3>
            <button onClick={() => setClosed(true)} data-testid={`cost-alert-close-${tipo}`} className="text-amber-500 hover:text-amber-700">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-amber-700 mt-0.5">
            {tipo === "ricette"
              ? "Il costo è salito per i rincari degli ingredienti."
              : "Il margine si è ridotto: valuta di ritoccare il prezzo di vendita."}
          </p>
          <ul className="mt-2 space-y-1">
            {items.slice(0, 5).map((it) => (
              <li key={it.id} className="text-sm text-amber-900 flex items-center gap-2" data-testid={`cost-alert-item-${it.id}`}>
                <TrendingUp className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                <span className="font-semibold truncate">{it.nome}</span>
                {tipo === "ricette" ? (
                  <span className="text-amber-700">
                    {it.costo_salvato.toFixed(2)}€/kg → <b>{it.costo_attuale.toFixed(2)}€/kg</b> (+{it.delta_pct.toFixed(0)}%)
                  </span>
                ) : (
                  <span className="text-amber-700">
                    food cost +{it.delta_pct.toFixed(0)}% · moltiplicatore {it.moltiplicatore_salvato.toFixed(1)}x → {it.moltiplicatore_attuale.toFixed(1)}x
                  </span>
                )}
              </li>
            ))}
          </ul>
          {items.length > 5 && (
            <p className="text-xs text-amber-600 mt-1">…e altri {items.length - 5}.</p>
          )}
        </div>
      </div>
    </div>
  );
}

/** Card riepilogo per la Dashboard + storico */
export function CostAlertCard({ localeId }) {
  const { data, forbidden } = useCostAlerts(localeId);
  const navigate = useNavigate();
  const [showStorico, setShowStorico] = useState(false);
  const [storico, setStorico] = useState(null);

  const openStorico = async () => {
    setShowStorico(true);
    try {
      const res = await axios.get(`${API}/costi/storico?giorni=180`, { headers: authHeaders() });
      setStorico(res.data);
    } catch { setStorico({ variazioni_prezzo: [], variazioni_food_cost: [] }); }
  };

  if (forbidden || !data) return null;
  const counts = data.counts || {};
  // La Dashboard "Allerta costi" evidenzia solo gli AUMENTI di costo (un calo non è un'allerta)
  const piatti = (data.piatti || []).filter((p) => (p.delta_pct || 0) > 0);
  const ricette = (data.ricette || []).filter((r) => (r.delta_pct || 0) > 0);
  const rincari = [...(data.rincari || [])].sort((a, b) => (b.delta_pct || 0) - (a.delta_pct || 0));
  // Dedup per articolo (più record nei 30gg) tenendo il rincaro maggiore
  const rincariUnici = [];
  const seenRincari = new Set();
  for (const r of rincari) {
    const k = r.item_id || r.item_nome;
    if (seenRincari.has(k)) continue;
    seenRincari.add(k);
    rincariUnici.push(r);
  }
  const cnt = { piatti: piatti.length, ricette: ricette.length, rincari: rincariUnici.length, totale: piatti.length + ricette.length };
  if (cnt.totale === 0 && rincariUnici.length === 0) return null;
  const soglia = data.soglia_pct ?? 5;
  const eur = (n) => `${Number(n || 0).toFixed(2)}€`;

  // Prezzo di vendita suggerito per ripristinare il margine % originale.
  const prezzoSuggerito = (fcAttuale, marginePct) => {
    if (!fcAttuale || marginePct >= 100) return null;
    const target = fcAttuale / (1 - marginePct / 100);
    return Math.ceil(target * 10) / 10; // arrotonda per eccesso a 0,10€
  };

  // Item "hero": il problema più grave da mostrare in evidenza.
  const heroPiatto = piatti[0] || null;
  const heroRicetta = !heroPiatto ? (ricette[0] || null) : null;

  // Serie per il grafico storico food cost (raggruppa per data, media delta)
  const chartData = (() => {
    if (!storico) return [];
    const all = [...(storico.variazioni_food_cost || []), ...(storico.variazioni_prezzo || [])];
    const byDate = {};
    all.forEach((v) => {
      const d = v.data || (v.created_at || "").slice(0, 10);
      if (!d) return;
      byDate[d] = byDate[d] || { data: d, n: 0, somma: 0 };
      byDate[d].n += 1;
      byDate[d].somma += Number(v.delta_pct || 0);
    });
    return Object.values(byDate)
      .map((x) => ({ data: x.data.slice(5), variazione: Math.round((x.somma / x.n) * 10) / 10 }))
      .sort((a, b) => a.data.localeCompare(b.data));
  })();

  const headerSub = heroPiatto
    ? `Margine in calo su ${cnt.piatti} piatt${cnt.piatti === 1 ? "o" : "i"}`
    : heroRicetta
      ? `Costo in aumento su ${cnt.ricette} ricett${cnt.ricette === 1 ? "a" : "e"}`
      : `${rincariUnici.length} rincaro/i materie prime (30gg)`;

  return (
    <div className="rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 p-5 mb-4" data-testid="cost-alert-card">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h3 className="font-bold text-amber-900 text-sm">Allerta costi</h3>
            <p className="text-xs text-amber-600" data-testid="cost-alert-subtitle">{headerSub}</p>
          </div>
        </div>
        <button onClick={openStorico} data-testid="cost-alert-storico-btn"
          className="flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-900 border border-amber-300 rounded-lg px-2.5 py-1.5 shrink-0 transition-colors">
          <LineChartIcon className="w-3.5 h-3.5" /> Storico
        </button>
      </div>

      {/* HERO: il problema più grave + azione consigliata */}
      {heroPiatto && (() => {
        const sugg = prezzoSuggerito(heroPiatto.food_cost_attuale, heroPiatto.margine_salvato);
        const aumentoPrezzo = sugg ? sugg - (heroPiatto.prezzo_vendita || 0) : 0;
        return (
          <div className="bg-white rounded-xl p-4 border border-amber-200 shadow-sm cursor-pointer hover:border-amber-400 hover:shadow-md transition-all"
            data-testid="cost-alert-hero"
            role="button"
            onClick={() => navigate(`/menu?open=${heroPiatto.id}`)}
            title="Apri il prodotto nel menù">
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="font-bold text-gray-900 truncate hover:underline" data-testid="cost-alert-hero-nome">{heroPiatto.nome}</span>
              <span className="text-xs font-bold text-red-600 bg-red-50 rounded-full px-2 py-0.5 shrink-0">
                food cost +{heroPiatto.delta_pct.toFixed(0)}%
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <div>
                <p className="text-[11px] text-gray-400 uppercase tracking-wide">Margine</p>
                <p className="font-semibold text-gray-700 flex items-center gap-1">
                  {heroPiatto.margine_salvato.toFixed(0)}%
                  <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-red-600">{heroPiatto.margine_attuale.toFixed(0)}%</span>
                </p>
              </div>
              <div className="h-8 w-px bg-gray-200" />
              <div>
                <p className="text-[11px] text-gray-400 uppercase tracking-wide">Food cost</p>
                <p className="font-semibold text-gray-700 flex items-center gap-1">
                  {eur(heroPiatto.food_cost_salvato)}
                  <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-red-600">{eur(heroPiatto.food_cost_attuale)}</span>
                </p>
              </div>
            </div>
            {sugg && aumentoPrezzo > 0.01 && (
              <div className="mt-3 flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-2.5" data-testid="cost-alert-suggestion">
                <Lightbulb className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <p className="text-xs text-emerald-800 leading-snug">
                  Per tornare al margine del <b>{heroPiatto.margine_salvato.toFixed(0)}%</b>, porta il prezzo da{" "}
                  <b>{eur(heroPiatto.prezzo_vendita)}</b> a <b>{eur(sugg)}</b>{" "}
                  <span className="text-emerald-600">(+{eur(aumentoPrezzo)})</span>.
                </p>
              </div>
            )}
          </div>
        );
      })()}

      {heroRicetta && (
        <div className="bg-white rounded-xl p-4 border border-amber-200 shadow-sm cursor-pointer hover:border-amber-400 hover:shadow-md transition-all"
          data-testid="cost-alert-hero"
          role="button"
          onClick={() => navigate(`/anagrafica/ricette?open=${heroRicetta.id}`)}
          title="Apri la ricetta">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="font-bold text-gray-900 truncate hover:underline" data-testid="cost-alert-hero-nome">{heroRicetta.nome}</span>
            <span className="text-xs font-bold text-red-600 bg-red-50 rounded-full px-2 py-0.5 shrink-0">+{heroRicetta.delta_pct.toFixed(0)}%</span>
          </div>
          <p className="text-sm text-gray-700 flex items-center gap-1">
            {eur(heroRicetta.costo_salvato)}/kg <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
            <span className="text-red-600 font-semibold">{eur(heroRicetta.costo_attuale)}/kg</span>
          </p>
          <div className="mt-3 flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-lg p-2.5">
            <Lightbulb className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
            <p className="text-xs text-emerald-800 leading-snug">
              Il costo di questa preparazione è salito: controlla i piatti che la usano e valuta di ritoccarne il prezzo.
            </p>
          </div>
        </div>
      )}

      {/* PERCHÉ: principali rincari materie prime/packaging (ultimi 30gg) */}
      {rincariUnici.length > 0 && (
        <div className="mt-3" data-testid="cost-alert-drivers">
          <p className="text-[11px] font-semibold text-amber-700 uppercase tracking-wide mb-1.5">
            Principali rincari (ultimi 30gg)
          </p>
          <ul className="space-y-1.5">
            {rincariUnici.slice(0, 3).map((r, i) => (
              <li key={`${r.item_id}-${i}`}
                className="flex items-center justify-between gap-2 text-sm cursor-pointer rounded-lg px-1 -mx-1 py-0.5 hover:bg-amber-100/60 transition-colors"
                data-testid="cost-alert-driver-item"
                role="button"
                title={r.macro === "packaging" ? "Apri il materiale/packaging" : "Apri l'ingrediente"}
                onClick={() => navigate(`/acquisti/prodotti?open=${r.item_id}&tab=${r.macro === "packaging" ? "materiali" : "food"}`)}>
                <span className="flex items-center gap-2 min-w-0">
                  {r.macro === "packaging"
                    ? <Package className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                    : <Carrot className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
                  <span className="font-medium text-gray-800 truncate hover:underline">{r.item_nome || "Voce"}</span>
                </span>
                <span className="text-gray-500 shrink-0 flex items-center gap-1">
                  {eur(r.prezzo_prec)} <ArrowRight className="w-3 h-3" /> {eur(r.prezzo_nuovo)}
                  <span className="text-red-600 font-semibold ml-1">+{Number(r.delta_pct).toFixed(0)}%</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Footer: riepilogo conteggi */}
      <div className="mt-4 pt-3 border-t border-amber-200/70 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-amber-700">
        <span data-testid="cost-alert-count-piatti"><b>{cnt.piatti || 0}</b> piatti</span>
        <span className="text-amber-300">·</span>
        <span data-testid="cost-alert-count-ricette"><b>{cnt.ricette || 0}</b> ricette</span>
        <span className="text-amber-300">·</span>
        <span data-testid="cost-alert-count-rincari"><b>{cnt.rincari || 0}</b> rincari (30gg)</span>
        <span className="text-amber-500 ml-auto">soglia &gt;{soglia}%</span>
      </div>

      <Dialog open={showStorico} onOpenChange={setShowStorico}>
        <DialogContent className="max-w-2xl" data-testid="cost-storico-dialog">
          <DialogHeader>
            <DialogTitle>Storico variazioni costi</DialogTitle>
          </DialogHeader>
          {chartData.length === 0 ? (
            <p className="text-sm text-gray-500 py-8 text-center">
              Nessuna variazione registrata ancora. Lo storico si popola man mano che i prezzi cambiano.
            </p>
          ) : (
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0e6d6" />
                  <XAxis dataKey="data" fontSize={11} />
                  <YAxis fontSize={11} unit="%" />
                  <Tooltip formatter={(v) => [`${v}%`, "Variazione media"]} />
                  <Line type="monotone" dataKey="variazione" stroke="#d97706" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
